import jsPDF from 'jspdf';
import { slugify } from '../lib/tableExport';

interface Participant {
  team_name: string;
  lane_number: number;
  /** Le dossard EST le code d'équipe (LOG-172). Sur une course, c'est ce qu'on
   *  lit au départ et sur le chrono — pas le nom. */
  bib_number?: string | null;
  /** Les athlètes de l'équipe, dans l'ordre. */
  participants?: string[] | null;
}

interface HeatWithParticipants {
  id: string;
  heat_number: number;
  start_time: string;
  date?: string; // Date du heat (format ISO YYYY-MM-DD)
  wod_name?: string;
  wod_number?: number;
  /** La ou les catégories de la heat (plusieurs sur un groupe de catégories). */
  categories?: string[];
  participants: Participant[];
}

/** Une case : un texte principal et, dessous, un complément plus petit et gris. */
type Case = { v: string; sub?: string; kind: 'hour' | 'team' | 'empty' };
type Ligne =
  | { band: string }
  | { subBand: string }
  | { cells: Case[] };

const NOIR: [number, number, number] = [17, 17, 17];
const GRIS: [number, number, number] = [110, 110, 110];
const FILET: [number, number, number] = [220, 220, 220];
const BANDEAU: [number, number, number] = [244, 244, 244];
// Le dégradé de la charte, ses trois arrêts.
const DEGRADE: [number, number, number][] = [[239, 68, 68], [249, 115, 22], [250, 204, 21]];

/**
 * Emojis retirés du PDF : une police de PDF ne les dessine pas, et un carré
 * vide à leur place ferait croire à une faute de frappe. Les noms d'équipe
 * restent lisibles sans (« Beauty and the Beast 🌸☀️ » → « Beauty and the
 * Beast »).
 */
const sansEmoji = (s: string) => s
  .replace(/\p{Extended_Pictographic}|\p{Emoji_Modifier}|‍|️|⃣/gu, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

const enBase64 = async (url: string): Promise<string> => {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url} : ${r.status}`);
  const octets = new Uint8Array(await r.arrayBuffer());
  let binaire = '';
  for (let i = 0; i < octets.length; i += 0x8000) {
    binaire += String.fromCharCode(...Array.from(octets.subarray(i, i + 0x8000)));
  }
  return btoa(binaire);
};

/**
 * Service d'export PDF des horaires des heats.
 */
export class HeatsPdfService {
  /**
   * Horaires des heats en PDF — course comme CrossFit (2026-09-28).
   *
   * Un vrai fichier et non plus une page à imprimer : Safari ignore
   * l'orientation demandée par une page (`@page { size: landscape }`) et
   * imprimait la grille en portrait, dix couloirs de 15 mm aux noms coupés en
   * syllabes. Un PDF porte son orientation, quel que soit le navigateur ; qui
   * veut l'imprimer l'imprime.
   *
   * Une seule fonction pour les deux sports : la STRUCTURE est identique (des
   * heats à l'heure, des équipes sur des couloirs), seul le vocabulaire change.
   *
   * A4 PAYSAGE, en grille : une ligne par heat, une colonne par couloir, de
   * largeur égale. Le WOD en bandeau, la catégorie en sous-titre quand elle
   * change. Inter et Anton embarquées : la typographie de la charte, et les
   * accents des noms rendus proprement.
   */
  static async downloadHeatsPdf(o: {
    eventName: string;
    heats: HeatWithParticipants[];
    selectedDate?: string;
    labels: { title: string; wave: string; hour: string; lane: string; footer: string };
  }): Promise<void> {
    const { eventName, heats, selectedDate, labels } = o;

    const { default: autoTable } = await import('jspdf-autotable');
    const [inter, interBold, anton, logo] = await Promise.all([
      enBase64('/fonts/Inter-Regular.ttf'),
      enBase64('/fonts/Inter-Bold.ttf'),
      enBase64('/fonts/Anton-Regular.ttf'),
      enBase64('/logos/LOGFIT-LOGO-ORANGE.png'),
    ]);

    const maxLane = Math.max(1, ...heats.flatMap((h) => h.participants.map((p) => p.lane_number)));
    // Au-delà de 12 couloirs, l'A4 ne laisse plus assez de largeur par nom.
    const nouveauDoc = () => {
      const d = new jsPDF({ orientation: 'landscape', unit: 'mm', format: maxLane > 12 ? 'a3' : 'a4' });
      d.addFileToVFS('Inter-Regular.ttf', inter);
      d.addFont('Inter-Regular.ttf', 'Inter', 'normal');
      d.addFileToVFS('Inter-Bold.ttf', interBold);
      d.addFont('Inter-Bold.ttf', 'Inter', 'bold');
      d.addFileToVFS('Anton-Regular.ttf', anton);
      d.addFont('Anton-Regular.ttf', 'Anton', 'normal');
      return d;
    };
    const doc = nouveauDoc();
    const largeur = doc.internal.pageSize.getWidth();
    const hauteur = doc.internal.pageSize.getHeight();
    const marge = 12;

    const jour = (d: string) => d
      ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined,
          { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : '';

    /**
     * Les athlètes, sous le nom d'équipe.
     *
     * Un athlète SEUL dont le nom est celui de l'équipe est tu : sur Farm Race,
     * 34 des 35 équipes solo portaient le nom de leur athlète, et la feuille
     * aurait imprimé « Adnan Kavak · Adnan Kavak » (règle du 2026-08-31).
     */
    const athletes = (p: Participant): string => {
      const noms = (p.participants || []).filter(Boolean);
      if (!noms.length) return '';
      const meme = (a: string, b: string) =>
        a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
      if (noms.length === 1 && meme(noms[0], p.team_name)) return '';
      return sansEmoji(noms.join(' · '));
    };

    const parJour = new Map<string, HeatWithParticipants[]>();
    heats.forEach((h) => {
      const j = h.date || '';
      parJour.set(j, [...(parJour.get(j) || []), h]);
    });
    const jours = Array.from(parJour.keys()).sort();

    // En-tête de document : titre Anton, contexte, logo, filet dégradé.
    const entete = () => {
      doc.setFont('Anton', 'normal'); doc.setFontSize(20); doc.setTextColor(...NOIR);
      doc.text(labels.title.toUpperCase(), marge, 17);
      doc.setFont('Inter', 'normal'); doc.setFontSize(10); doc.setTextColor(...GRIS);
      doc.text(selectedDate ? `${eventName} · ${jour(selectedDate)}` : eventName, marge, 23);
      const hLogo = 9;
      doc.addImage(logo, 'PNG', largeur - marge - hLogo * (839 / 411), 14, hLogo * (839 / 411), hLogo);
      const pas = (largeur - 2 * marge) / 120;
      for (let i = 0; i < 120; i++) {
        const t = i / 119;
        const [a, b, u] = t < 0.5 ? [DEGRADE[0], DEGRADE[1], t * 2] : [DEGRADE[1], DEGRADE[2], (t - 0.5) * 2];
        doc.setFillColor(...(a.map((c, k) => Math.round(c + (b[k] - c) * u)) as [number, number, number]));
        doc.rect(marge + i * pas, 26, pas + 0.05, 0.7, 'F');
      }
    };

    jours.forEach((d, indexJour) => {
      if (indexJour > 0) doc.addPage();
      entete();
      let y = 31;
      if (jours.length > 1 && !selectedDate && d) {
        doc.setFont('Inter', 'bold'); doc.setFontSize(11); doc.setTextColor(...NOIR);
        doc.text(jour(d), marge, y + 2);
        y += 6;
      }

      const lignes: Ligne[] = [];
      let wodCourant: string | null = null;
      let categorieCourante: string | null = null;
      [...(parJour.get(d) || [])]
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')
                        || a.heat_number - b.heat_number)
        .forEach((h) => {
          // Bande par épreuve : en CrossFit un même créneau enchaîne plusieurs
          // WODs, et c'est le repère qu'on cherche sur la feuille.
          const wod = h.wod_name || '';
          if (wod !== wodCourant) {
            wodCourant = wod;
            categorieCourante = null;
            if (wod) lignes.push({ band: sansEmoji(wod).toUpperCase() });
          }
          // La catégorie en sous-titre, posée quand elle change : des heats qui
          // se suivent sont presque toujours de la même catégorie.
          const categorie = (h.categories || []).filter(Boolean).join(' + ');
          if (categorie && categorie !== categorieCourante) {
            categorieCourante = categorie;
            lignes.push({ subBand: sansEmoji(categorie) });
          }

          const parLane = new Map<number, Participant>();
          h.participants.forEach((p) => parLane.set(p.lane_number, p));
          const cells: Case[] = [{
            v: (h.start_time || '').slice(0, 5), sub: `${labels.wave} ${h.heat_number}`, kind: 'hour',
          }];
          for (let lane = 1; lane <= maxLane; lane++) {
            const p = parLane.get(lane);
            cells.push(p
              ? { v: sansEmoji(`${p.bib_number ? p.bib_number + '  ' : ''}${p.team_name}`),
                  sub: athletes(p) || undefined, kind: 'team' }
              : { v: '—', kind: 'empty' });
          }
          lignes.push({ cells });
        });

      const nbCol = maxLane + 1;
      const casesParLigne: (Case | null)[][] = [];
      const body = lignes.map((l) => {
        if ('band' in l) {
          casesParLigne.push([null]);
          return [{ content: l.band, colSpan: nbCol, styles: {
            font: 'Anton', fontStyle: 'normal' as const, fontSize: 10.5, fillColor: BANDEAU, textColor: NOIR,
          } }];
        }
        if ('subBand' in l) {
          casesParLigne.push([null]);
          return [{ content: l.subBand, colSpan: nbCol, styles: {
            fontStyle: 'bold' as const, fontSize: 8.5, textColor: GRIS, cellPadding: { top: 2.2, bottom: 1.2, left: 2, right: 2 },
          } }];
        }
        casesParLigne.push(l.cells);
        // Le complément est écrit dans le contenu pour que la hauteur de la
        // ligne le compte ; il est redessiné plus petit et gris après coup.
        return l.cells.map((c) => ({
          content: c.sub ? `${c.v}\n${c.sub}` : c.v,
          styles: c.kind === 'hour' ? { fontStyle: 'bold' as const }
            : c.kind === 'empty' ? { textColor: GRIS } : {},
        }));
      });

      const TAILLE_SUB = 6.8;
      const PAD = 1.8;
      const tete = [labels.hour, ...Array.from({ length: maxLane }, (_, i) => `${labels.lane} ${i + 1}`)];
      const estTitre = (i: number) => 'band' in lignes[i] || 'subBand' in lignes[i];

      /**
       * Dessine les lignes [debut, fin) à partir de `y0`, sur la page courante
       * de `d`, et note dans `pages` la page où tombe chaque ligne.
       */
      const tableau = (d: jsPDF, y0: number, debut: number, fin: number, pages: number[]) => {
        autoTable(d, {
          startY: y0,
          head: [tete],
          body: body.slice(debut, fin),
          theme: 'plain',
          margin: { left: marge, right: marge, top: 14, bottom: 14 },
          tableWidth: largeur - 2 * marge,
          styles: {
            font: 'Inter', fontSize: 8.5, textColor: NOIR, cellPadding: PAD,
            lineColor: FILET, lineWidth: 0.2, overflow: 'linebreak', valign: 'top',
          },
          headStyles: { fontStyle: 'bold', fontSize: 7, textColor: GRIS, fillColor: [255, 255, 255] },
          // L'heure garde sa colonne, les couloirs se partagent le reste à
          // parts égales : la grille affichée au mur reste alignée.
          columnStyles: Object.fromEntries([
            [0, { cellWidth: 20 }],
            ...Array.from({ length: maxLane }, (_, i) =>
              [i + 1, { cellWidth: (largeur - 2 * marge - 20) / maxLane }]),
          ]),
          rowPageBreak: 'avoid',
          willDrawCell: (data) => {
            if (data.section !== 'body') return;
            const c = casesParLigne[debut + data.row.index]?.[data.column.index];
            // Le complément est dans le contenu pour que la hauteur de ligne
            // le compte : on ne dessine ici que le texte principal.
            if (c?.sub) data.cell.text = d.splitTextToSize(c.v, data.cell.width - 2 * PAD);
          },
          didDrawCell: (data) => {
            if (data.section !== 'body') return;
            if (data.column.index === 0) pages[debut + data.row.index] = d.getCurrentPageInfo().pageNumber;
            const c = casesParLigne[debut + data.row.index]?.[data.column.index];
            if (!c?.sub) return;
            // Le complément (numéro de heat, athlètes), plus petit et gris.
            const w = data.cell.width - 2 * PAD;
            const principal = d.splitTextToSize(c.v, w) as string[];
            const interligne = (data.cell.styles.fontSize / d.internal.scaleFactor) * 1.15;
            const ySub = data.cell.y + PAD + principal.length * interligne + 0.6;
            d.setFont('Inter', 'normal'); d.setFontSize(TAILLE_SUB); d.setTextColor(...GRIS);
            d.text(d.splitTextToSize(c.sub, w) as string[], data.cell.x + PAD,
                   ySub + (TAILLE_SUB / d.internal.scaleFactor) * 0.9, { lineHeightFactor: 1.15 });
          },
        });
      };

      /** Le tableau entier, avec un saut de page avant chaque ligne de `coupures`. */
      const dessiner = (d: jsPDF, coupures: number[]): number[] => {
        const pages: number[] = [];
        const bornes = [0, ...coupures, lignes.length];
        for (let k = 0; k < bornes.length - 1; k++) {
          if (k > 0) d.addPage();
          tableau(d, k === 0 ? y : 14, bornes[k], bornes[k + 1], pages);
        }
        return pages;
      };

      // Un titre (WOD, catégorie) ne reste jamais seul en bas de page : une
      // mise en page d'essai repère ceux que la ligne suivante a quittés, et
      // on passe à la page avant eux. On recommence tant qu'il en reste
      // (déplacer un titre peut en isoler un autre plus loin).
      const coupures: number[] = [];
      for (let essai = 0; essai < 20; essai++) {
        const brouillon = nouveauDoc();
        const pages = dessiner(brouillon, coupures);
        const orphelin = lignes.findIndex((_, i) =>
          estTitre(i) && i + 1 < lignes.length && pages[i] !== pages[i + 1] && !coupures.includes(i));
        if (orphelin < 0) break;
        // Un bandeau de WOD suivi d'un sous-titre part avec lui.
        const debutBloc = orphelin > 0 && 'band' in lignes[orphelin - 1] ? orphelin - 1 : orphelin;
        coupures.push(debutBloc);
        coupures.sort((m, n) => m - n);
      }
      dessiner(doc, coupures);
    });

    // Pied de chaque page : la mention et la pagination.
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFont('Inter', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...GRIS);
      doc.text(labels.footer, largeur / 2, hauteur - 6, { align: 'center' });
      doc.text(`${i} / ${pages}`, largeur - marge, hauteur - 6, { align: 'right' });
    }

    doc.save(`${slugify(labels.title)}-${slugify(eventName)}${selectedDate ? `-${selectedDate}` : ''}.pdf`);
  }

  /**
   * Récupère les participants de tous les heats — en UNE requête.
   *
   * Ce service en faisait une PAR heat : sur un événement à 80 vagues, 80
   * allers-retours pour imprimer une feuille. C'est le défaut corrigé le
   * 2026-08-20 côté planning, resté ici.
   *
   * Un échec est remonté (2026-09-28) : un PDF sans les équipes, produit sans
   * le dire, serait affiché au mur comme s'il était complet.
   */
  static async fetchHeatsWithParticipants(
    eventId: string,
    heats: any[]
  ): Promise<HeatWithParticipants[]> {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://events.logfit.eu/api/v1';
    const parHeat = new Map<string, Participant[]>();

    const response = await fetch(`${apiUrl}/events/${eventId}/heat-participants`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`équipes indisponibles (${response.status})`);
    }
    const data = await response.json();
    (data.heat_participants || []).forEach((p: any) => {
      const liste = parHeat.get(p.heat_id) || [];
      liste.push({
        team_name: p.team_name,
        lane_number: p.lane_number,
        bib_number: p.bib_number ?? null,
        participants: p.participants ?? null,
      });
      parHeat.set(p.heat_id, liste);
    });

    return heats.map((heat) => ({
      id: heat.id,
      heat_number: heat.heat_number,
      start_time: heat.start_time,
      date: heat.date,
      wod_name: heat.wod_name,
      wod_number: heat.wod_number,
      // GET /heats sert `category_names` sur un groupe, `category_name` sinon.
      categories: heat.category_names
        ?? (heat.category_name ? [heat.category_name] : []),
      participants: parHeat.get(heat.id) || [],
    }));
  }
}
export default HeatsPdfService;
